import {
    Button,
    Collapse,
    NumberInput,
    TextInput,
    ActionIcon,
} from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import React, { useEffect, useState, useMemo } from "react";
import { BASE_URL } from "../api/EndPoints";
import "./GeDetails.css";
import notify from "../utils/Notification";
import { createB2BAPI } from "../api/Interceptor";

const GeDetails = ({ selectedStatusType, purchaseOrder, activeTabTop }) => {
    const [openLogistics, setOpenLogistics] = useState(false);
    const [formData, setFormData] = useState({});
    const [isDownloading, setIsDownloading] = useState(false);
    const ge = useMemo(() => purchaseOrder?.ge || null, [purchaseOrder?.ge]);
    const isDisabled = !!ge;
    const B2B_API = createB2BAPI();

    const GeFields = useMemo(
        () => [
            { type: "TextInput", label: "GE No", name: "geNo" },
            { type: "NumberInput", label: "No of Rolls Received", name: "nofRollsReceived" },
            { type: "NumberInput", label: "No of Qty Received", name: "nofQtyReceived" },
            purchaseOrder?.orderType === "WHOLESALE"
                ? { type: "TextInput", label: "Received Container Number", name: "receivedContainerNumber" }
                : { type: "TextInput", label: "Received Carton Number", name: "receivedCartonNumber" },
            { type: "TextInput", label: "ASN Checked", name: "asnChecked" },
            { type: "TextInput", label: "LR Copy Verified", name: "lrCopyVerified" },
        ],
        [purchaseOrder]
    );

    // ✅ Backend download (for PDFs or docs)
    const handleDownload = async (filePath, fileLabel) => {
        if (!filePath) return;
        setIsDownloading(true);

        try {
            const encodedPath = encodeURIComponent(filePath);
            const res = await B2B_API.get(`vendor/download?filePath=${encodedPath}`, {
                responseType: "blob",
            });

            const blob = await res.blob();
            const contentDisposition = res.headers.get("content-disposition");
            const fileName =
                contentDisposition?.split("filename=")[1]?.replace(/"/g, "") ||
                filePath.split("/").pop() ||
                `${fileLabel || "document"}.pdf`;

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            notify({
                title: "Download Failed!",
                message: error?.message || "Unable to download file.",
                error: true,
                success: false,
            });
        } finally {
            setIsDownloading(false);
        }
    };

    useEffect(() => {
        if (ge) {
            setFormData({
                geNo: ge.geNo || "",
                asnNo: ge.asnNo || "",
                nofRollsReceived: ge.nofRollsReceived || "",
                nofQtyReceived: ge.nofQtyReceived || "",
                receivedContainerNumber: ge.receivedContainerNumber || "",
                receivedCartonNumber: ge.receivedCartonNumber || "",
                receivedBy: ge.receivedBy || "",
                asnChecked: ge.asnChecked ? "Yes" : "No",
                lrCopyVerified: ge.lrCopyVerified ? "Yes" : "No",
                driverImage: ge.driverImage || "",
                vehicleImage: ge.vehicleImage || "",
                asnDocumentUrl: ge.asnDocumentUrl || "",
                lrDocumentUrl: ge.lrDocumentUrl || "",
            });
        } else {
            setFormData({});
        }
    }, [ge]);

    const renderField = (field, key) => {
        const commonProps = {
            value: formData[field.name] ?? "",
            disabled: isDisabled,
            placeholder: field.placeholder,
            label: <strong>{field.label}</strong>,
        };

        switch (field.type) {
            case "NumberInput":
                return <NumberInput key={key} {...commonProps} />;
            case "TextInput":
                return <TextInput key={key} {...commonProps} />;
            default:
                return null;
        }
    };

    const renderGEFiles = (label, filePath) => {
        if (!filePath) return null;

        const fullUrl = filePath.startsWith("http")
            ? filePath
            : `${BASE_URL}${filePath.replace("/api", "")}`;

        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fullUrl);
        const fileName = filePath.split("/").pop();

        return (
            <div
                key={label}
                style={{
                    marginBottom: "1rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                }}
            >
                <div>
                    <strong>{label}</strong>
                </div>

                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "10px",
                    }}
                >
                    <div>
                        {isImage ? (
                            <img
                                src={fullUrl}
                                alt={`${label}?time=${Date.now()}`}
                                style={{
                                    width: "150px",
                                    height: "100px",
                                    objectFit: "cover",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                }}
                            />
                        ) : (
                            <TextInput
                                value={fileName}
                                readOnly
                                variant="filled"
                                styles={{
                                    input: {
                                        fontSize: "0.9rem",
                                        color: "#868e96",
                                        opacity: "0.6",
                                        width: "420px",
                                        border:"1px solid #ced4da",
                                        backgroundColor: "#f1f3f5",
                                        cursor: "not-allowed",
                                        pointerEvents: "none",
                                    },
                                }}
                            />

                        )}
                    </div>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <ActionIcon
                            onClick={() => handleDownload(filePath, label)}
                            variant="filled"
                            color="#8BC53F"
                            size={26}
                            loading={isDownloading}
                        >
                            <IconDownload size={18} color="white" />
                        </ActionIcon>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="Ge-view">
                {selectedStatusType !== "NEW" && activeTabTop === "GE" && (
                    <Button
                        onClick={() => setOpenLogistics((prev) => !prev)}
                        style={{ background: "#ab1fa4ff" }}
                    >
                        GE Details
                    </Button>
                )}
            </div>

            <Collapse in={openLogistics} transitionDuration={300}>
                <div className="Ge-fields">
                    {GeFields.map((field) => renderField(field, field.name))}

                    {renderGEFiles("Driver Image", purchaseOrder?.ge?.driverImage)}
                    {renderGEFiles("Vehicle Image", purchaseOrder?.ge?.vehicleImage)}
                    {renderGEFiles("ASN Document", purchaseOrder?.ge?.asnDocumentUrl)}
                    {renderGEFiles("LR Document", purchaseOrder?.ge?.lrDocumentUrl)}
                </div>
            </Collapse>
        </>
    );
};

export default GeDetails;
